/**
 * Partner app shell — redesigned per glos-partner-redesign.html. Owns its
 * own visual system (colors/fonts) scoped under .partner-app, distinct
 * from the admin theme; see PartnerRedesign.css.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useApi } from '../../lib/api';
import usePolling from '../../lib/usePolling';
import { GreatLeapLogoIcon } from '../../components/GreatLeapLogoIcon';
import '../../styles/PartnerRedesign.css';

const NAV_ITEMS = [
  { to: '/partner', end: true, label: 'Overview', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>
  ) },
  { to: '/partner/clients', label: 'Clients', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20"/><circle cx="9" cy="7" r="3.2"/><path d="M22 20v-1.5a4 4 0 0 0-3-3.8"/><path d="M16.5 4.2a4 4 0 0 1 0 5.6"/></svg>
  ) },
  { to: '/partner/tier', label: 'My Tier', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="8.5" r="5.5"/><path d="M8.2 13.3L7 22l5-2.6 5 2.6-1.2-8.7"/></svg>
  ) },
  { to: '/partner/billing', label: 'Billing', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><rect x="3" y="4" width="18" height="16" rx="2.5"/><path d="M3 9.5h18M7.5 14.5h4"/></svg>
  ) },
];

function userInitials(user) {
  if (!user) return '?';
  return ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase() || user.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || '?';
}

export default function PartnerLayout() {
  const api = useApi();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [partner, setPartner] = useState(null);
  const [counts, setCounts] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.me()
      .then((r) => alive && setMe(r))
      .catch((err) => alive && setError(err?.response?.data?.error || err.message));
    return () => { alive = false; };
  }, [api]);

  const location = useLocation();

  // Reloads the profile, client counts and cycles behind the sidebar badges.
  const refreshPartner = React.useCallback(() => {
    api.partnerMe().then((r) => { setPartner(r.partner); setCounts(r.counts); }).catch(() => {});
    api.partnerListCycles().then((r) => setCycles(r.items || [])).catch(() => {});
  }, [api]);

  // Refresh on every page change so badges ("1 due", client count) never
  // lag behind what the partner just did on another page.
  useEffect(() => {
    if (!me?.isPartner) return;
    refreshPartner();
  }, [me, location.pathname, refreshPartner]);
  usePolling(refreshPartner, 60000, !!me?.isPartner);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const clientTotal = counts ? (counts.active || 0) + (counts.pending || 0) + (counts.unlinked || 0) : 0;
  // Due = open cycles inside their 25th–5th submission window (from the API).
  const openCycles = useMemo(() => cycles.filter((c) => c.status === 'open' && c.window_open), [cycles]);
  const dueCycles = openCycles;

  if (error) return <div style={{ padding: 32, color: '#B4363B' }}>Failed to load partner area: {error}</div>;
  if (!me)   return <div style={{ padding: 32, color: '#9ca3af' }}>Loading…</div>;
  if (!me.isPartner) {
    return (
      <div style={{ padding: 32, maxWidth: 520, margin: '40px auto' }}>
        <h2 style={{ color: '#0f1c2e' }}>Partner access required</h2>
        <p style={{ color: '#6b7280' }}>This area is restricted to approved GLOS partners.</p>
        <Link to="/dashboard" className="btn-primary" style={{ display: 'inline-flex', marginTop: 12 }}>
          Back to My Workspaces
        </Link>
      </div>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const suspended = partner?.status === 'suspended';

  return (
    <div className={`partner-app${collapsed ? ' rail-mini' : ''}`}>
      <div className="app">
        <aside className="rail">
          <div className="rail-top">
            <div className="mark"><GreatLeapLogoIcon variant="dark" size={22} /></div>
            <button
              type="button"
              className="rail-toggle"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                {collapsed ? <path d="M9 6l6 6-6 6M4 6l6 6-6 6" /> : <path d="M15 6l-6 6 6 6M20 6l-6 6 6 6" />}
              </svg>
            </button>
          </div>

          <nav className="nav">
            {NAV_ITEMS.map(({ to, end, label, icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `nav-item${isActive ? ' on' : ''}`}
              >
                {icon}
                <span className="lbl">{label}</span>
                {label === 'Clients' && clientTotal > 0 && <span className="nav-tag">{clientTotal}</span>}
                {label === 'Billing' && dueCycles.length > 0 && <span className="nav-tag alert">{dueCycles.length} due</span>}
              </NavLink>
            ))}
          </nav>

          {openCycles[0] && (
            <div className="rail-note">
              <h5>{new Date(openCycles[0].period_month).toLocaleDateString('en-IN', { month: 'long' })} invoice pending</h5>
              <p>Upload it by {(() => { const [y, m, d] = (openCycles[0].window_closes_on || '').split('-').map(Number); return y ? new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : 'the 5th'; })()} so it can move into admin review.</p>
            </div>
          )}

          <div className="who" ref={menuRef}>
            <div className="ring">
              <svg viewBox="0 0 38 38">
                <defs><linearGradient id="rg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#2F6BFF"/><stop offset="1" stopColor="#16C79A"/></linearGradient></defs>
                <circle cx="19" cy="19" r="17" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="2.4"/>
                <circle cx="19" cy="19" r="17" fill="none" stroke="url(#rg)" strokeWidth="2.4" strokeLinecap="round" strokeDasharray="107" strokeDashoffset="64" transform="rotate(-90 19 19)"/>
              </svg>
              <span>{userInitials(user)}</span>
            </div>
            <button type="button" style={{ minWidth: 0, textAlign: 'left', flex: 1 }} onClick={() => setMenuOpen((o) => !o)}>
              <div className="who-meta">
                <b>{user?.firstName} {user?.lastName}</b>
                <small>{partner?.tier_name ? `${partner.tier_name} partner · ${partner.tier_commission_percentage}%` : me.email}</small>
              </div>
            </button>

            {menuOpen && (
              <div className="who-menu">
                <Link to="/profile" onClick={() => setMenuOpen(false)}>My Profile</Link>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)}>← My Workspaces</Link>
                <button type="button" onClick={handleSignOut}>Sign out</button>
              </div>
            )}
          </div>
        </aside>

        <main className="main">
          {suspended && (
            <div className="admin-banner error" style={{ margin: '20px 36px 0' }}>
              Your partner account is suspended{partner?.suspension_reason ? `: ${partner.suspension_reason}` : ''}.
              You can still view historical data, but active operations are disabled.
            </div>
          )}
          <Outlet context={{ partner, counts, cycles, refreshPartner }} />
        </main>
      </div>
    </div>
  );
}
